<template>
  <div>
    <Head :title="__(title)" />
    <div class="max-w-full overflow-hidden bg-white rounded-md shadow">
      <form @submit.prevent="store">
        <div class="flex flex-wrap p-8 -mb-8 -mr-6">

          <!-- Nom -->
          <text-input
            v-model="form.last_name"
            :error="form.errors.last_name"
            class="w-full pb-8 pr-6 lg:w-1/3"
            :label="__('Nom')"
          />

          <!-- Prénom -->
          <text-input
            v-model="form.first_name"
            :error="form.errors.first_name"
            class="w-full pb-8 pr-6 lg:w-1/3"
            :label="__('Prénom')"
          />

          <!-- Fonction -->
          <text-input
            v-model="form.title"
            :error="form.errors.title"
            class="w-full pb-8 pr-6 lg:w-1/3"
            :label="__('Fonction')"
          />

          <!-- Email -->
          <text-input
            v-model="form.email"
            :error="form.errors.email"
            class="w-full pb-8 pr-6 lg:w-1/3"
            :label="__('Email')"
          />

          <!-- Email de notification -->
          <text-input
            v-model="form.second_email"
            :error="form.errors.second_email"
            class="w-full pb-8 pr-6 lg:w-1/3"
            :label="__('Email de notification')"
          />

          <!-- Téléphone -->
          <text-input
            v-model="form.phone"
            :error="form.errors.phone"
            class="w-full pb-8 pr-6 lg:w-1/3"
            :label="__('Numéro de téléphone professionnel')"
          />

          <!-- Rôle -->
          <select-input
            v-model="form.role_id"
            :error="form.errors.role_id"
            class="w-full pb-8 pr-6 lg:w-1/3"
            :label="__('Rôle')"
          >
            <option :value="null">-- Sélectionner --</option>
            <option v-for="r in roles" :key="r.id" :value="r.id">{{ r.name }}</option>
          </select-input>

          <!-- Compte - system_admin uniquement, AVANT institution -->
          <div v-if="is_system_admin" class="w-full pb-8 pr-6 lg:w-1/3">
            <label class="form-label">{{ __('Compte') }}</label>
            <select
              v-model="form.current_account_id"
              class="w-full form-input"
              :class="{ 'border-red-400': form.errors.current_account_id }"
              @change="onAccountChange"
            >
              <option :value="null">-- Sélectionner --</option>
              <option v-for="a in accounts" :key="a.id" :value="a.id">{{ a.name }}</option>
            </select>
            <div v-if="form.errors.current_account_id" class="form-error">
              {{ form.errors.current_account_id }}
            </div>
          </div>

          <!-- Institution de rattachement - filtrée par compte si system_admin -->
          <div class="w-full pb-8 pr-6 lg:w-1/3">
            <label class="form-label">{{ __('Institution de rattachement') }}</label>
            <select
              v-model="form.department_id"
              class="w-full form-input"
              :class="{ 'border-red-400': form.errors.department_id }"
              :disabled="is_system_admin && !form.current_account_id"
            >
              <option :value="null">
                {{
                  is_system_admin && !form.current_account_id
                    ? '-- Sélectionner un compte d\'abord --'
                    : '-- Sélectionner --'
                }}
              </option>
              <option
                v-for="d in filteredDepartments"
                :key="d.id"
                :value="d.id"
              >
                {{ d.name }}
              </option>
            </select>
            <div v-if="form.errors.department_id" class="form-error">
              {{ form.errors.department_id }}
            </div>
            <!-- Indicateur de chargement -->
            <div v-if="loadingDepartments" class="mt-1 text-xs text-gray-400 flex items-center gap-1">
              <svg class="animate-spin h-3 w-3" viewBox="0 0 24 24" fill="none">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
              </svg>
              {{ __('Chargement...') }}
            </div>
          </div>

          <!-- Commune - system_admin uniquement -->
          <select-input
            v-if="is_system_admin"
            v-model="form.municipality_id"
            :error="form.errors.municipality_id"
            class="w-full pb-8 pr-6 lg:w-1/3"
            :label="__('Commune')"
          >
            <option :value="null">-- Sélectionner --</option>
            <option v-for="m in communes" :key="m.id" :value="m.id">{{ m.name }}</option>
          </select-input>

          <!-- Mot de passe - info -->
          <div class="w-full pb-8 pr-6 lg:w-1/3 flex items-center">
            <div class="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700 w-full">
              <svg class="w-4 h-4 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zm-1 4a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V10a1 1 0 00-1-1z" clip-rule="evenodd"/>
              </svg>
              <span>{{ __('Un mot de passe sécurisé sera généré automatiquement et envoyé par email à l\'utilisateur.') }}</span>
            </div>
          </div>

        </div>

        <div class="flex items-center justify-end px-8 py-4 border-t border-gray-100 bg-gray-50">
          <loading-button :loading="form.processing" class="btn-indigo" type="submit">
            {{ __('Créer') }}
          </loading-button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { Head } from '@inertiajs/vue3'
import axios from 'axios'
import Layout from '@/Shared/Layout'
import TextInput from '@/Shared/TextInput'
import SelectInput from '@/Shared/SelectInput'
import LoadingButton from '@/Shared/LoadingButton'

export default {
  components: { Head, LoadingButton, SelectInput, TextInput },
  layout: Layout,

  props: {
    title:           { type: String },
    roles:           { type: Array, default: () => [] },
    departments:     { type: Array, default: () => [] },  // departments du compte courant (non system_admin)
    communes:        { type: Array, default: () => [] },
    accounts:        { type: Array, default: () => [] },
    is_system_admin: { type: Boolean, default: false },
  },

  remember: 'form',

  data() {
    return {
      // Pour system_admin : liste chargée dynamiquement selon le compte choisi
      // Pour les autres : on utilise directement la prop departments
      dynamicDepartments: [],
      loadingDepartments: false,

      form: this.$inertia.form({
        first_name:          '',
        last_name:           '',
        title:               '',
        phone:               '',
        email:               '',
        second_email:        '',
        department_id:       null,
        role_id:             null,
        municipality_id:     null,
        current_account_id:  null,
      }),
    }
  },

  computed: {
    /**
     * system_admin → liste chargée dynamiquement via l'API
     * autres rôles  → prop departments passée par le contrôleur
     */
    filteredDepartments() {
      return this.is_system_admin ? this.dynamicDepartments : this.departments
    },
  },

  methods: {
    /**
     * Déclenché au changement de compte (system_admin uniquement).
     * Réinitialise le department_id et recharge les departments du compte.
     */
    async onAccountChange() {
      // Reset du department sélectionné
      this.form.department_id = null
      this.dynamicDepartments = []

      if (!this.form.current_account_id) return

      this.loadingDepartments = true
      try {
        const { data } = await axios.get(
          this.route('filter.departments_by_account'),
          { params: { account_id: this.form.current_account_id } }
        )
        this.dynamicDepartments = data
      } catch (e) {
        console.error('Erreur chargement departments :', e)
      } finally {
        this.loadingDepartments = false
      }
    },

    store() {
      this.form.post(this.route('users.store'))
    },
  },
}
</script>